/**
 * ProtectedRoute
 *
 * Redirects to /login when there's no authenticated session, preserving
 * the originally-requested location so LoginPage can send the user back
 * after signing in.
 *
 * Also enforces the WIP-module-specific access list, now backed by the
 * standalone WIP Permissions Service (see usePermissionsService.js) --
 * a signed-in user marked "restricted" there is genuinely blocked here,
 * checked fresh from a shared store on every load (not per-browser
 * localStorage, so it's consistent across devices). If the permissions
 * service itself is unreachable, this FAILS OPEN (treats the user as
 * allowed) rather than locking everyone out of the whole app because a
 * small side-service happens to be down.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import { useCurrentUserAccess } from '../hooks/usePermissionsService.js';

export const ProtectedRoute = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();
  const { access, isChecking } = useCurrentUserAccess(isAuthenticated ? user?.id : null);

  if (isLoading || isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-edge border-t-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (access === 'restricted') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-base px-4 text-center">
        <ShieldOff className="h-10 w-10 text-rose-500" />
        <p className="text-base font-semibold text-primary">Access Restricted</p>
        <p className="max-w-sm text-sm text-muted">
          Your access to this WIP module has been restricted. Contact an administrator if you
          believe this is a mistake.
        </p>
        <button
          type="button"
          onClick={logout}
          className="mt-2 rounded-md border border-edge px-3 py-1.5 text-sm text-secondary hover:bg-surface-hover"
        >
          Sign out
        </button>
      </div>
    );
  }

  return <Outlet />;
};
